import React, { useEffect, useMemo } from 'react'
import { parseYoutubeVideoId, toYoutubeEmbedUrl } from '../lib/youtubeUrl'

interface HuayLiveModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  youtubeUrl: string
  playerStartMuted?: boolean
}

export const HuayLiveModal: React.FC<HuayLiveModalProps> = ({
  isOpen,
  onClose,
  title,
  youtubeUrl,
  playerStartMuted = true,
}) => {
  const videoId = useMemo(() => (youtubeUrl ? parseYoutubeVideoId(youtubeUrl) : null), [youtubeUrl])

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return
    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 960,
          background: '#0f172a',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
            color: '#fff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(255,255,255,0.18)',
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 0 0 0 rgba(255,255,255,0.7)',
                  animation: 'huayLiveModalPulse 1.4s infinite',
                }}
              />
              LIVE
            </span>
            <span
              style={{
                fontWeight: 700,
                fontSize: 16,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </span>
          </div>
          <button
            type="button"
            aria-label="ปิด"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: '#fff',
              width: 32,
              height: 32,
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="fas fa-times" />
          </button>
        </div>
        <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
          {videoId ? (
            <iframe
              title={title}
              src={toYoutubeEmbedUrl(videoId, { autoplay: true, muted: playerStartMuted })}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
            />
          ) : (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fca5a5',
                fontSize: 14,
              }}
            >
              ไม่สามารถเล่นวิดีโอนี้ได้ (ลิงก์ไม่ถูกต้อง)
            </div>
          )}
        </div>
        <style>{`
          @keyframes huayLiveModalPulse {
            0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
            70% { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
            100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
          }
        `}</style>
      </div>
    </div>
  )
}

export default HuayLiveModal
